import { ButtonHTMLAttributes, CSSProperties, DetailedHTMLProps } from 'react'
import { Spinner } from 'components/ui/Spinner'
import clsx from 'clsx'
import { useGetSetting } from 'api/settings'

export type ButtonVariant = 'primary' | 'secondary' | 'tertiary' | 'negative' | 'positive'
export type ButtonSize = 'md' | 'sm'

const BASE =
  'tw-inline-flex tw-cursor-pointer tw-items-center tw-justify-center tw-gap-2 tw-whitespace-nowrap tw-transition-all tw-duration-200'

const SIZE: Record<ButtonSize, string> = {
  // md: default height preserves the previous button size.
  md: 'tw-h-10 tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-rounded-md',
  // sm: compact pill for inline actions (chat cards, reminders).
  sm: 'tw-px-5 tw-py-1.5 tw-text-xs tw-font-medium tw-rounded-full',
}

// primary has no bg class: it stays tenant-themed via an inline color (classes can't hold a dynamic value).
const VARIANT: Record<ButtonVariant, string> = {
  primary: 'tw-text-white',
  secondary: 'tw-bg-[var(--input-bg)] tw-text-[var(--text-primary)]',
  tertiary: 'tw-bg-transparent tw-text-[var(--text-primary)]',
  negative: 'tw-bg-red-500 tw-text-white',
  positive: 'tw-bg-green-500 tw-text-white',
}

export type UIButtonProps = DetailedHTMLProps<ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement> & {
  loading?: boolean
  variant?: ButtonVariant
  size?: ButtonSize
  color?: string
}

export const UIButton = ({
  children,
  loading,
  variant,
  size = 'md',
  color,
  className,
  disabled,
  style,
  ...rest
}: UIButtonProps) => {
  const { setting } = useGetSetting()
  const resolvedVariant: ButtonVariant = variant ?? 'primary'
  const interactive = !disabled && !loading

  const primaryStyle: CSSProperties =
    resolvedVariant === 'primary' ? { backgroundColor: color || setting?.primaryColor } : {}

  return (
    <button
      className={clsx(
        BASE,
        SIZE[size ?? 'md'],
        VARIANT[resolvedVariant],
        interactive ? 'hover:tw-opacity-80 active:tw-scale-[0.98]' : 'tw-cursor-not-allowed tw-opacity-70',
        className,
      )}
      style={{ ...primaryStyle, ...style }}
      disabled={disabled || loading}
      {...rest}
    >
      {loading ? <Spinner /> : children}
    </button>
  )
}
